import { noItx } from "../../../src/translationItx/itx";
import {
  getEloItemCatalog,
  getCatalogProfileItemIds,
  resolveCatalogEntryFromItems,
  type C1_EloItemCatalogData,
  type C1_EloItemCatalogEntry,
} from "../data/C1_EloItemCatalog";
import type { C1_LightProductDefinition } from "../data/C1_ProductDefinitions";
import type { C1_LightUsageCategory } from "../data/C1_ProductDefinitions";
import BaseModel from "../../../src/models/BaseModel";
import BaseModelMap from "../../../src/models/BaseModelMap";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import type { C1_DefinitionItemInput } from "../types/C1_DefinitionTypes";
import { C1_DefinitionItem } from "./C1_DefinitionItem";
import { C1_DefinitionsTreeViewModel } from "./C1_DefinitionTreeViewModels";

export type C1_LightUsageCategoryDefinition = {
  description: { de: string; en: string };
};

/** Owns the expanded root definitions of the runtime ELO catalog. */
export class C1_Definitions extends BaseModel {
  readonly roots = new BaseModelMap<C1_DefinitionItem>(C1_DefinitionItem, this, "roots", false);

  constructor(parent?: BaseModel) {
    super({ storeInObjectRegistry: false, isPersistent: false }, parent);
    this.isPersistent = false;
    this.roots.name = "ELO-Definitionen";
    this.roots.treeViewModel = new C1_DefinitionsTreeViewModel(this.roots);
  }

  override get className(): string {
    return "C1_Definitions";
  }

  get(id: string): C1_DefinitionItem | undefined {
    return this.roots.get(id);
  }
}

/** Non-persistent runtime representation of all Concept One catalog data. */
export class C1_Catalog extends BaseModel {
  readonly definitions = new C1_Definitions(this);
  readonly lightTypes = new Map<string, C1_LightProductDefinition>();
  readonly lightUsageCategories = new Map<string, C1_LightUsageCategoryDefinition>();
  readonly profiles = new Map<string, string[]>();
  version = 0;

  constructor() {
    super({ storeInObjectRegistry: false, isPersistent: false });
    this.isPersistent = false;
  }

  override get className(): string {
    return "C1_Catalog";
  }

  profile(profileId: string): C1_DefinitionItem[] {
    const profile = this.definitions.get(profileId);
    return profile?.children.asArray() ?? [];
  }

  /** Restores the non-persistent definition lookup after the project registry was cleared. */
  registerDefinitionsInObjectRegistry(): void {
    const registerDefinition = (definition: C1_DefinitionItem): void => {
      state.objectRegistry.registerObject(definition);
      for (const child of definition.children) registerDefinition(child);
    };

    for (const root of this.definitions.roots) registerDefinition(root);
  }
}

/** Expands one JSON definition and all referenced subitems into an owned tree. */
function expandDefinition(
  id: string,
  items: Record<string, C1_EloItemCatalogEntry>,
  resolving: Set<string>,
): C1_DefinitionItemInput | undefined {
  const rawSource = items[id];
  if (!rawSource) {
    console.error(noItx(`[C1 definitions] Unknown definition reference "${id}".`));
    return undefined;
  }
  if (resolving.has(id)) {
    console.error(noItx(`[C1 definitions] Circular definition reference "${id}".`));
    return undefined;
  }

  const source = resolveCatalogEntryFromItems(items, id);
  if (!source) {
    console.error(noItx(`[C1 definitions] Invalid extends chain for definition "${id}".`));
    return undefined;
  }

  const nextResolving = new Set(resolving);
  nextResolving.add(id);
  const children: C1_DefinitionItemInput[] = [];
  for (const childId of source.items ?? []) {
    const child = expandDefinition(childId, items, nextResolving);
    if (child) children.push(child);
  }

  const isGroup = source.items !== undefined;
  return {
    ...source,
    definitionId: id,
    type:
      isGroup && source.type !== C1_EloItem_Type.PlugGroup
        ? C1_EloItem_Type.Group
        : source.type!,
    hasTag: source.hasTag ?? true,
    description: {
      de: source.description?.de || id,
      en: source.description?.en || id,
    },
    children: children.length > 0 ? children : undefined,
  };
}

/** Builds one profile root and expands all of its referenced item definitions. */
function expandProfileDefinition(
  profileId: string,
  definitionIds: string[],
  items: Record<string, C1_EloItemCatalogEntry>,
): C1_DefinitionItemInput {
  const children: C1_DefinitionItemInput[] = [];
  // Lights and arrays switch mounting while retaining their ESTADA identity. All mounting
  // variants must be resolvable after reload, even though the palette creates only the default.
  const availableIds = new Set(definitionIds);
  if (["electricalLight", "outdoorLight", "lightGroup", "outdoorLightGroup"].includes(profileId)) {
    for (const id of Object.keys(items)) {
      if (id.startsWith(`${profileId}.`)) availableIds.add(id);
    }
  }
  for (const definitionId of availableIds) {
    const definition = expandDefinition(definitionId, items, new Set<string>());
    if (definition) children.push(definition);
  }

  return {
    definitionId: profileId,
    type: C1_EloItem_Type.Group,
    hasTag: false,
    defaultEnabled: true,
    description: { de: profileId, en: profileId },
    includeInDescription: false,
    children,
  };
}

/** Loads the bundled JSON catalog into independent non-persistent model trees. */
export function loadDefinitions(source: C1_EloItemCatalogData = getEloItemCatalog()): C1_Catalog {
  const catalog = new C1_Catalog();
  catalog.version = source.version;

  for (const id of Object.keys(source.lightTypes)) catalog.lightTypes.set(id, source.lightTypes[id]);
  for (const id of Object.keys(source.lightUsageCategories)) {
    catalog.lightUsageCategories.set(id, source.lightUsageCategories[id]);
  }
  // Instances can reference reusable items directly, e.g. lightGroup.ceiling and
  // lightGroup.ceiling/lightGroup.ceiling.light, rather than a palette profile.
  for (const id of Object.keys(source.items)) {
    if (Object.prototype.hasOwnProperty.call(source.profiles, id)) continue;
    const definition = expandDefinition(id, source.items, new Set<string>());
    if (definition) catalog.definitions.roots.add(new C1_DefinitionItem(definition, catalog.definitions.roots));
  }
  for (const id of Object.keys(source.profiles)) {
    const definitionIds = getCatalogProfileItemIds(source.profiles[id]);
    catalog.profiles.set(id, definitionIds);
    const profile = expandProfileDefinition(id, definitionIds, source.items);
    catalog.definitions.roots.add(new C1_DefinitionItem(profile, catalog.definitions.roots));
  }

  return catalog;
}

export type { C1_LightUsageCategory };
